import moment from 'moment/moment';
import _ from 'lodash';

import './UserActivityCtrl.css';

export function UserActivityRoute($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.loggedin.UserActivity', {
		url: '/admin/users/activity',
		controllerAs: 'vm',
		controller: UserActivityCtrl,
		templateUrl: '/ng/hub/User/Activity/UserActivityCtrl.html?' + window.BUILD,
		permission: ['manage_users'],
		resolve: {
			$title: () => 'User Activity Report',
		},
	});
}

export class UserActivityCtrl {
	constructor($scope, Api, Cfg, Identity, UrlData) {
		'ngInject';
		// save injections
		this.Api = Api;
		this.Cfg = Cfg;
		this.Identity = Identity;
		this.UrlData = UrlData;
		$scope.$watch('vm.sortBy', this.sortUsers.bind(this));
		$scope.$on('$destroy', () => (this.hasUserNavigatedAway = true));
		// setup
		this.settings = _.extend(
			{
				dateStart: moment().subtract(30, 'days').format('YYYY-MM-DD'),
				dateEnd: moment().format('YYYY-MM-DD'),
				role: '!observer',
				scope: 'current',
				across: 'all',
				divisions: [],
			},
			this.UrlData.getAll()
		);

		this.hideLikesAndComments = this.Cfg.read('Features.hide_likes_and_comments') !== '0';
		this.reportTypes = [
			'Logins',
			'SiteVisits',
			'PostSubmissions',
			'PostViews',
			'EmailsSent',
			'Searches',
			'DocumentsDownloads',
		];
		if (!this.hideLikesAndComments) {
			this.reportTypes.push('PostComments');
			this.reportTypes.push('PostLikes');
		}
		this.roles = [
			{ name: 'with any role', value: '!observer' },
			{ name: 'with role Editor/Intel Leader', value: 'intel leader' },
			{ name: 'with role Client', value: 'executive' },
			{ name: 'with role Member', value: 'member' },
		];
		// filter by hub type
		this.hubScopes = [{ id: 'current', name: 'for users in this hub' }];
		// TODO: the API only accepts `hub` or `all` for the `across` param
		// if (Identity.isSuperadmin()) {
		// 	this.hubScopes.push({ id: 'account', name: 'for users in this account' });
		// }
		if (Identity.hasMultipleHubs()) {
			this.hubScopes.push({ id: 'mine', name: 'for users in all my hubs' });
		}
		this.acrossOptions = [
			{ id: 'all', name: 'User activity' },
			{ id: 'hub', name: 'Hub activity' },
		];
		this.sortBy = 'EMAIL';
		this.limit = 100;
		this.showDivisionFilter = this.Cfg.readAsBool('UI.divisions_filter');
		if (this.showDivisionFilter) {
			this.setupDivisions();
		}
		this.showTable = false;
	}

	async setupDivisions() {
		try {
			// construct hubs header
			const headers = {
				Hubs: this.settings.scope,
			};
			const data = await this.Api.get('/analytics/divisions', {}, headers);
			if (data.length > 0) {
				this.divisionChoices = [...data];
			}
			const label = this.Cfg.read('UI.divisions_filter_label').trim();
			// this is to handle cases where users might set the division label to something like Department TEST.
			// then it will display as department TEST on the multiselect dropdown to look like the other ones...
			const lowerCasedLabel = label.charAt(0).toLowerCase() + label.slice(1);
			this.userActivityProps = { placeholder: lowerCasedLabel };
		} catch (e) {
			return [];
		}
	}

	lookupRole(value) {
		const role = _.find(this.roles, { value });
		return role ? role.name : 'All Roles';
	}

	lookupScope(id) {
		const scope = _.find(this.hubScopes, { id });
		return scope ? scope.name : 'Include: this hub';
	}

	getReportTitle() {
		const title = [];
		if (this.settings.across == 'hub') {
			title.push('Hub activity');
		} else {
			title.push('User activity');
		}
		title.push(
			'for',
			moment(this.settings.dateStart).format('MMM D, YYYY'),
			'-',
			moment(this.settings.dateEnd).format('MMM D, YYYY')
		);
		if (this.settings.scope == 'current') {
			title.push('for users in hub');
			title.push(this.Identity.currentClient().name);
		} else if (this.settings.scope == 'account') {
			title.push('for users in account');
			title.push(this.Identity.currentClient().account);
		} else if (this.settings.scope == 'mine') {
			title.push('for all users');
		}
		if (this.settings.role != '!observer') {
			title.push(this.lookupRole(this.settings.role));
		}
		return title.join(' ');
	}

	run() {
		this.reportTitle = this.getReportTitle();
		this.totals = null;
		this.UrlData.setAll(this.settings);
		this.loadActivity();
	}

	sortUsers() {
		if (!this.users) {
			return;
		}
		let mult = 1;
		let sortBy = this.sortBy;
		const isDescending = sortBy.slice(0, 1) == '-';
		if (isDescending) {
			mult = -1;
			sortBy = sortBy.slice(1);
		}
		this.users.sort((a, b) => {
			if (a[sortBy] < b[sortBy]) return -1 * mult;
			if (a[sortBy] > b[sortBy]) return 1 * mult;
			return 0;
		});
	}

	getPercent() {
		return Math.round(((this.totalFetched || 0) * 100) / (this.totalToFetch || 1));
	}

	async loadActivity(page = 1) {
		this.hasStarted = true;
		if (page == 1) {
			this.totalFetched = 0;
			this.users = [];
		}
		this.isLoading = true;
		this.hasMoreUsers = true;
		const params = {
			limit: this.limit,
			page,
			role: this.settings.role,
			sort: 'email',
			include_deleted: true,
		};
		const headers = {
			Hubs: this.settings.scope,
		};

		if (this.settings.divisions.length > 0 && this.showDivisionFilter) {
			params.divisions = this.settings.divisions.join(',');
		} else {
			params.divisions = 'any';
		}

		const resp = await this.Api.getPaginated('/users/search', params, headers);
		let users = resp.data;
		this.totalToFetch = resp.total * this.reportTypes.length;

		if (this.hasUserNavigatedAway) return;

		// check if we are done (be sure you do this before you filter out duplicates)
		if (users.length === 0) {
			this.isLoading = false;
			this.hasMoreUsers = false;
			return;
		}

		if (resp.isLastPage) {
			this.hasMoreUsers = false;
		}

		// ensure we don't have any duplicate user ids
		const currIds = this.users.map(u => u.id);
		users = users.filter(u => {
			return currIds.indexOf(u.id) == -1;
		});

		users.forEach(u => {
			u.last_login_formatted = u.last_login ? moment(u.last_login).format('MM/DD/YYYY HH:mma') : 'N/A';
			u.FNAME = u.fname.toLocaleUpperCase();
			u.LNAME = u.lname.toLocaleUpperCase();
			u.FPREFERRED = u.fname_preferred ? u.fname_preferred.toLocaleUpperCase() : '';
			u.LPREFERRED = u.lname_preferred ? u.lname_preferred.toLocaleUpperCase() : '';
			u.EMAIL = u.email.toLocaleUpperCase();
			// The division property isn't always on the user object, gotta check for it.
			if (u.division) {
				u.DIVISION = u.division.toLocaleUpperCase();
			}
		});

		users.forEach(u => this.users.push(u));
		const ids = users.map(u => u.id).join(',');

		// get stat columns for each user
		for (let i = 0; i < this.reportTypes.length; i++) {
			// TODO: `this.settings.across` is always `all` unless you load the page with `across=hub`.
			/// changing the dropdown does not affect this value
			if (this.settings.across === 'hub' && this.reportTypes[i] === 'Logins') {
				this.totalFetched += users.length;
				users.forEach(u => (u.Logins = 'N/A'));
				continue;
			}

			const countsByUserId = await this.Api.get(`/users/activity/${this.reportTypes[i]}`, {
				user_ids: ids,
				start: this.settings.dateStart + ' 00:00:00',
				end: this.settings.dateEnd + ' 23:59:59',
				across: this.settings.across,
			});

			this.totalFetched += users.length;
			if (this.hasUserNavigatedAway) return;

			users.forEach(u => {
				u[this.reportTypes[i]] = Number(countsByUserId[u.id]) || 0;
			});
		}

		if (!resp.isLastPage) {
			await new Promise(resolve => setTimeout(resolve, 40));
			this.loadActivity(page + 1);
		} else {
			this.isLoading = false;
			this.calcTotals();
		}
	}

	calcTotals() {
		const totals = {
			email: 'Totals:',
			fname: '',
			lname: '',
			fname_preferred: '',
			lname_preferred: '',
			is_active: '',
			division: '',
			last_login_formatted: '',
			Logins: 0,
			SiteVisits: 0,
			PostSubmissions: 0,
			PostComments: 0,
			PostLikes: 0,
			PostViews: 0,
			EmailsSent: 0,
			Searches: 0,
			DocumentsDownloads: 0,
		};
		this.reportTypes.forEach(type => {
			this.users.forEach(u => (totals[type] += u[type]));
		});
		if (this.settings.across == 'hub') {
			totals.Logins = '';
		}
		this.totals = totals;
	}

	async download() {
		const data = {
			email: [],
			fname: [],
			lname: [],
			fname_preferred: [],
			lname_preferred: [],
			is_active: [],
			last_login_formatted: [],
			Logins: [],
			SiteVisits: [],
			PostSubmissions: [],
			PostViews: [],
			EmailsSent: [],
			Searches: [],
			DocumentsDownloads: [],
		};

		if (!this.hideLikesAndComments) {
			data.PostComments = [];
			data.PostLikes = [];
		}

		if (this.showDivisionFilter) {
			data.division = [];
		}

		const mapActiveColumn = value => (value ? 'Active' : 'Inactive');
		const keys = Object.keys(data);
		this.users.forEach((user, i) => {
			keys.forEach(key => {
				data[key][i] = key === 'is_active' ? mapActiveColumn(user[key]) : user[key];
			});
		});

		data.email.push('Totals');
		data.fname.push('');
		data.lname.push('');
		data.fname_preferred.push('');
		data.lname_preferred.push('');
		data.is_active.push('');
		if (this.showDivisionFilter) {
			data.division.push('');
		}
		data.last_login_formatted.push('');
		data.Logins.push(this.totals.Logins);
		data.SiteVisits.push(this.totals.SiteVisits);
		data.PostSubmissions.push(this.totals.PostSubmissions);

		if (!this.hideLikesAndComments) {
			data.PostComments.push(this.totals.PostComments);
			data.PostLikes.push(this.totals.PostLikes);
		}

		data.PostViews.push(this.totals.PostViews);
		data.Searches.push(this.totals.Searches);
		data.DocumentsDownloads.push(this.totals.DocumentsDownloads);
		this.generatingSpreadsheet = true;

		const ss = await this.Api.post('/users/activity/spreadsheet', {
			data,
			title: this.reportTitle,
			generatedAt: moment().format('MM/DD/YYYY hh:mm a'),
		});

		this.generatingSpreadsheet = false;

		if (ss && ss.url) {
			window.location.href = ss.url;
		} else {
			alert('Error generating spreadsheet.');
		}
	}
}
